// KPIs de un proyecto. Fichero plano en src/ como utils.js y api.js, que es la
// convencion del repo.
//
// Todo son funciones PURAS y sin JSX: asi las puede consumir tambien un PDF, y
// se pueden ejecutar con node para probarlas (npm run pruebas).
//
// Regla que se repite en todo el fichero: cuando no hay dato se devuelve `null`,
// nunca `0`. Es la misma disciplina que el indice de productividad del cuaderno,
// cuyo comentario ya advierte que "un 0 se leeria como improductivo, no como sin
// datos". La interfaz pinta «—» donde hay null.

import { PJ_ESTADOS, OBJETIVO_DIA } from "./constants";
import { pasosDeTrabajo, avancePasos, avanceEsfuerzo } from "./utils";
import { diasLaborablesEntre, hoyISO, diasEntre, sumarDias, esFindeISO }
  from "./components/timeline/timelineScale";
import { detectCycles } from "./components/flow/flowGeometry";

export { pasosDeTrabajo, avancePasos, avanceEsfuerzo };

// ── Esfuerzo: previsto frente a real ─────────────────────────────────────────
// Devuelve el total y el detalle por paso, ordenado por desvio absoluto: lo que
// interesa ver primero es lo que mas se ha desviado, en cualquier direccion.
export function desvioEsfuerzo(tareas = [], minutosPorTarea = {}) {
  let est = 0, real = 0;
  const porPaso = [];
  for (const t of pasosDeTrabajo(tareas)) {
    const e = Number(t.estimacion_min) || 0;
    const r = Number(minutosPorTarea[String(t.id)]) || 0;
    est += e; real += r;
    // Un paso sin estimar no tiene desvio: no se le puede comparar con nada.
    porPaso.push({
      id: String(t.id), title: t.title, status: t.status,
      est: e, real: r,
      ratio: e ? r / e : null,
      pct: e ? Math.round((r / e - 1) * 100) : null,
    });
  }
  porPaso.sort((a, b) => {
    if (a.pct === null && b.pct === null) return 0;
    if (a.pct === null) return 1;
    if (b.pct === null) return -1;
    return Math.abs(b.pct) - Math.abs(a.pct);
  });
  return {
    est, real,
    ratio: est ? real / est : null,
    pct:   est ? Math.round((real / est - 1) * 100) : null,
    porPaso,
    // Cuantos pasos no se pueden juzgar. Se devuelve para poder avisarlo en vez
    // de dar un total que parece completo y no lo es.
    sinEstimar: porPaso.filter(p => !p.est).length,
  };
}

// El plan de horas del proyecto, en minutos: la SUMA de lo estimado en sus pasos.
//
// Hubo tambien una estimacion escrita a mano en el proyecto, y se quito: eran dos
// numeros para lo mismo que podian discrepar, y el de arriba no se enteraba de
// que alguien añadiera un paso. El plan de un proyecto es lo que suman sus pasos
// y no hay una segunda opinion que mantener.
//
// La misma regla esta en SQL, en el `plan_min` de backend/routes/proyectos.js
// para las tarjetas del listado. La duplicacion es inevitable porque el listado
// no trae las tareas; si cambia una, cambia la otra.
//
// Devuelve null y no 0 cuando nadie ha estimado: la interfaz escribe «Sin plan»,
// que no es lo mismo que un plan de cero horas.
export function planMinutos(tareas = []) {
  const suma = desvioEsfuerzo(tareas).est;
  return suma > 0 ? suma : null;
}

// ── Plazo ────────────────────────────────────────────────────────────────────
// Desvio en DIAS LABORABLES entre la fecha de fin prevista y el cierre real.
//
// El cierre es: la fecha de cierre si esta completada, hoy si vencio y sigue
// abierta, y nada si aun esta en plazo — un paso que no ha vencido no tiene
// desvio, y meterlo como 0 mezclaria "a tiempo" con "sin evaluar".
export function desvioPlazo(tareas = [], festivos = {}, hoy = hoyISO()) {
  const porPaso = [];
  for (const t of tareas) {
    if ((t.kind || "tarea") !== "tarea" && t.kind !== "hito") continue;
    const fin = t.due_date;
    if (!fin) continue;

    let cierre = null;
    if (t.status === "Completada") {
      cierre = (t.completed_at ? String(t.completed_at).slice(0, 10) : null) || fin;
    } else if (t.status !== "Cancelada" && fin < hoy) {
      cierre = hoy;
    }
    if (!cierre) continue;

    // diasLaborablesEntre cuenta ambos extremos, asi que cerrar el mismo dia
    // que vencia da 1; se resta para que "en plazo" sea 0.
    const bruto = diasLaborablesEntre(fin, cierre, festivos);
    const d = bruto === 0 ? 0 : (bruto > 0 ? bruto - 1 : bruto + 1);
    porPaso.push({
      id: String(t.id), title: t.title, status: t.status, kind: t.kind,
      due_date: fin, cierre, dias: d,
    });
  }
  porPaso.sort((a, b) => Math.abs(b.dias) - Math.abs(a.dias));
  const tarde = porPaso.filter(p => p.dias > 0);
  return {
    porPaso,
    // El desvio del proyecto es el PEOR, no la media: un proyecto va tarde lo
    // que va tarde su paso mas retrasado, no el promedio de todos.
    max:   porPaso.length ? Math.max(...porPaso.map(p => p.dias)) : null,
    media: tarde.length
      ? Math.round(tarde.reduce((s, p) => s + p.dias, 0) / tarde.length * 10) / 10
      : null,
    cuantosTarde: tarde.length,
    evaluados: porPaso.length,
  };
}

// Dias laborables, pero moviendose por el calendario en vez de contando. El
// tope de vueltas no es paranoia: `festivos` viene del servidor y un festivo mal
// cargado que tapara la semana entera dejaria el bucle sin salida.
const TOPE = 3650;
const noSeTrabaja = (iso, festivos) => esFindeISO(iso) || !!festivos[iso];

// El primer dia laborable desde uno dado, el mismo si ya lo es.
function laborable(iso, festivos) {
  if (!iso) return iso;
  let d = iso;
  for (let i = 0; i < TOPE && noSeTrabaja(d, festivos); i++) d = sumarDias(d, 1);
  return d;
}
const siguienteLaborable = (iso, festivos) => iso ? laborable(sumarDias(iso, 1), festivos) : iso;

function sumarLaborables(iso, n, festivos) {
  let d = laborable(iso, festivos);
  for (let i = 0; i < n; i++) d = siguienteLaborable(d, festivos);
  return d;
}

// ── Cuando va a acabar esto de verdad ────────────────────────────────────────
// La fecha de fin PREVISTA del proyecto: una pasada hacia adelante por el grafo
// de pasos, en dias laborables y con lo que ya ha pasado dentro.
//
// No se calcula aplicando "la desviacion" como un numero al final. La desviacion
// sale sola si cada nodo se resuelve con su realidad y empuja a los que dependen
// de el: un paso que va cuatro dias tarde pero que NO esta en el camino hacia el
// final no retrasa el proyecto, y sumarle su retraso al total seria mentir.
//
//   completado  -> su cierre real, que es un hecho y no una prevision
//   cancelado   -> no dura ni estorba: pasa por el lo que le llegue de atras
//   hito        -> SUELO, no techo: max(su fecha, lo que llegue de atras). Un
//                  hito futuro sin trabajo delante impide acabar antes (no hay
//                  proyecto que termine antes de su comite), y a un hito al que
//                  se llega tarde se le ve llegar tarde.
//   control     -> no dura: repite lo que le llega
//   trabajo     -> empieza cuando puede —sus predecesores, su fecha, y nunca
//                  antes de HOY, que es la regla que mete el retraso real en la
//                  cuenta— y dura lo planificado
//
// Devuelve null y un motivo antes que un numero inventado: con un bucle en el
// flujo no hay orden en que recorrerlo, y sin una sola fecha ni estimacion en
// todo el proyecto lo que saldria seria "hoy + lo que me invente".
export function previsionFin(tareas = [], dependencias = [], {
  festivos = {}, hoy = hoyISO(), inicio = null,
} = {}) {
  const nodos = {};
  for (const t of tareas || []) nodos[String(t.id)] = t;
  const ids = Object.keys(nodos);
  const vacio = { fecha: null, motivo: "sin-datos", determina: null, sinEstimar: 0 };
  if (!ids.length) return vacio;

  const aristas = {};
  (dependencias || []).forEach((d, i) => { aristas[String(d.id ?? i)] = d; });

  // Con un bucle no hay orden topologico y cualquier numero seria inventado. Es
  // la misma respuesta que ya da la ruta critica, y por eso se usa su detector.
  if (detectCycles(nodos, aristas).length)
    return { fecha: null, motivo: "ciclo", determina: null, sinEstimar: 0 };

  const salidas = {}, entradas = {}, grado = {};
  ids.forEach(id => { salidas[id] = []; entradas[id] = []; grado[id] = 0; });
  for (const e of Object.values(aristas)) {
    const f = String(e.from_task_id), t = String(e.to_task_id);
    if (!(f in salidas) || !(t in grado)) continue;
    salidas[f].push(t); entradas[t].push(f); grado[t]++;
  }

  const orden = [];
  let cola = ids.filter(id => grado[id] === 0);
  while (cola.length) {
    const siguiente = [];
    for (const id of cola) {
      orden.push(id);
      for (const t of salidas[id]) if (--grado[t] === 0) siguiente.push(t);
    }
    cola = siguiente;
  }

  // Un proyecto sin una sola fecha ni estimacion no se puede prever. Se dice,
  // en vez de devolver hoy mas un dia por paso, que es lo que saldria.
  const hayDato = (tareas || []).some(t =>
    t.start_date || t.due_date || Number(t.estimacion_min) > 0);
  if (!hayDato && !inicio) return vacio;

  const mayor = (a, b) => (!a ? b : !b ? a : (a > b ? a : b));
  let sinEstimar = 0;

  const fin = {}, ocupa = {};
  for (const id of orden) {
    const t = nodos[id];
    const kind = t.kind || "tarea";

    // Cuando le llega el turno: cuando acaba el ultimo de sus predecesores.
    //
    // El dia siguiente lo gana solo quien viene detras de algo que OCUPA dias.
    // Dos pasos de trabajo no comparten el dia en que uno acaba y el otro
    // empieza, pero un hito se alcanza el mismo dia en que termina el paso que
    // lleva a el, y el trabajo que cuelga del nodo de inicio arranca el dia en
    // que arranca el proyecto, no al siguiente.
    let base = null, baseTrabajo = null;
    for (const p of entradas[id]) {
      base = mayor(base, fin[p]);
      baseTrabajo = mayor(baseTrabajo,
        fin[p] && ocupa[p] ? siguienteLaborable(fin[p], festivos) : fin[p]);
    }

    if (kind === "hito") { fin[id] = mayor(t.due_date || t.start_date || null, base); continue; }
    if (t.status === "Completada") {
      fin[id] = (t.completed_at ? String(t.completed_at).slice(0, 10) : null) || t.due_date || base;
      ocupa[id] = true;
      continue;
    }
    if (t.status === "Cancelada") { fin[id] = base; continue; }
    if (kind !== "tarea") {
      // El nodo de inicio es el unico control que APORTA una fecha, y es la del
      // proyecto: es donde arranca todo lo que no depende de nadie.
      fin[id] = kind === "inicio" ? mayor(base, inicio) : base;
      continue;
    }

    const arranque = mayor(mayor(baseTrabajo, t.start_date || null), hoy);
    let dias;
    if (t.start_date && t.due_date) dias = Math.abs(diasLaborablesEntre(t.start_date, t.due_date, festivos)) || 1;
    else if (Number(t.estimacion_min) > 0) dias = Math.max(1, Math.ceil(Number(t.estimacion_min) / 60 / OBJETIVO_DIA));
    else { dias = 1; sinEstimar++; }
    fin[id] = sumarLaborables(laborable(arranque, festivos), dias - 1, festivos);
    ocupa[id] = true;
  }

  // Lo que manda es el camino mas largo, y quien lo marca merece decirse: "lo
  // marca Impartir formacion" responde la pregunta siguiente sin tener que ir a
  // buscarla por la lista.
  let fecha = null, determina = null;
  for (const id of ids) {
    if (!fin[id] || (fecha && fin[id] <= fecha)) continue;
    fecha = fin[id];
    determina = (nodos[id].kind || "tarea") === "tarea" || nodos[id].kind === "hito"
      ? { id, title: nodos[id].title } : determina;
  }
  if (!fecha) return vacio;
  return { fecha, motivo: null, determina, sinEstimar };
}

// ── Embudo por estado ────────────────────────────────────────────────────────
// Los cinco estados de un paso, en el orden en que se avanza. "Atascado" es
// estar en espera o bloqueado desde hace mas de N dias: un paso bloqueado ayer
// no es un problema, uno bloqueado hace tres semanas si.
export const DIAS_ATASCO = 5;

export function embudo(tareas = [], hoy = hoyISO(), diasAtasco = DIAS_ATASCO) {
  const trabajo = pasosDeTrabajo(tareas);
  const conteo = {};
  for (const e of PJ_ESTADOS) conteo[e] = 0;
  let cancelados = 0;
  for (const t of trabajo) {
    if (t.status === "Cancelada") { cancelados++; continue; }
    if (conteo[t.status] === undefined) conteo[t.status] = 0;
    conteo[t.status]++;
  }
  const atascados = trabajo.filter(t => {
    if (t.status !== "En Espera" && t.status !== "Bloqueada") return false;
    const desde = t.status_changed_at || t.updated_at;
    if (!desde) return false;
    return diasEntre(String(desde).slice(0, 10), hoy) > diasAtasco;
  });
  return {
    conteo, cancelados,
    total: trabajo.length - cancelados,
    atascados: atascados.map(t => ({
      id: String(t.id), title: t.title, status: t.status,
      dias: diasEntre(String(t.status_changed_at || t.updated_at).slice(0, 10), hoy),
      nota: t.nota_bloqueo || null,
    })).sort((a, b) => b.dias - a.dias),
  };
}

// ── Carga por persona ────────────────────────────────────────────────────────
// `periodo` es { desde, hasta } y sirve para calcular la capacidad real: los
// dias laborables del tramo por las horas de jornada. Sin periodo no se calcula
// ocupacion, porque dividir por una capacidad inventada da un porcentaje que
// parece medido y no lo es.
export function cargaPersona(tareas = [], minutosPorPersona = {}, users = [],
                             periodo = null, festivos = {}, objetivoDia = OBJETIVO_DIA) {
  const nombre = id => users.find(u => String(u.id) === String(id))?.name || "?";

  const filas = {};
  const toca = (uid) => {
    const k = String(uid);
    if (!filas[k]) filas[k] = {
      user_id: k, name: nombre(k), pasos: 0, pendientes: 0,
      estPend: 0, est: 0, real: 0,
    };
    return filas[k];
  };

  for (const t of pasosDeTrabajo(tareas)) {
    const e = Number(t.estimacion_min) || 0;
    const abierto = t.status !== "Completada" && t.status !== "Cancelada";
    // Solo los ENCARGADOS cargan trabajo. Un responsable a quien preguntar no
    // esta ocupado por ese paso, y contarlo doblaria la carga del equipo.
    for (const a of t.asignados || []) {
      if (a.rol !== "encargado") continue;
      const f = toca(a.user_id);
      f.pasos++;
      f.est += e;
      if (abierto) { f.pendientes++; f.estPend += e; }
    }
  }

  // Las horas reales pueden ser de gente que ya no tiene pasos asignados: se
  // cuentan igual, o el total del proyecto no cuadraria con la suma de la tabla.
  for (const [uid, mins] of Object.entries(minutosPorPersona || {})) {
    toca(uid).real = Number(mins) || 0;
  }

  const laborables = periodo?.desde && periodo?.hasta
    ? diasLaborablesEntre(periodo.desde, periodo.hasta, festivos)
    : null;
  const capacidad = laborables ? laborables * objetivoDia * 60 : null;

  const totalReal = Object.values(filas).reduce((s, f) => s + f.real, 0);

  return Object.values(filas).map(f => ({
    ...f,
    saldo: f.est ? f.real - f.est : null,
    pctReal: totalReal ? Math.round(f.real / totalReal * 100) : null,
    capacidad,
    ocupacion: capacidad && f.estPend ? f.estPend / capacidad : null,
  })).sort((a, b) => b.real - a.real || b.estPend - a.estPend);
}

// Bandas de ocupacion. Se marca en rojo a quien va por encima y en ambar a quien
// va muy por debajo: las dos cosas son informacion para quien reparte.
export const bandaOcupacion = o => {
  if (o === null || o === undefined) return null;
  if (o > 1.10) return { key: "alta",  label: "Sobrecargado", color: "#ef4444" };
  if (o >= 0.85) return { key: "ok",    label: "Ajustado",     color: "#22c55e" };
  return { key: "baja", label: "Con holgura", color: "#f59e0b" };
};

// ── Calendario frente a trabajo ──────────────────────────────────────────────
// Cuanto calendario se ha consumido ya, frente a cuanto trabajo se ha hecho.
// Es la pregunta "¿voy adelantado o atrasado?", y se contesta en PORCENTAJE.
//
// No confundir con desvioPlazo(), que mide otra cosa distinta: alli el retraso
// son DIAS LABORABLES de pasos concretos que vencieron. Aqui no se habla nunca
// de dias, y alli no se habla nunca de porcentaje.
//
// `avance` lo pone quien llama, porque cada vista tiene el suyo a mano: el
// listado pasa el progreso_efectivo que ya calcula SQL y el detalle pasa el
// avance de esfuerzo. Puede venir null: entonces se devuelve el calendario
// consumido igualmente, pero sin veredicto.
export function calendarioVsTrabajo(proyecto, avance = null, festivos = {}, hoy = hoyISO()) {
  if (!proyecto?.start_date || !proyecto?.end_date) return null;

  const totalDias = diasLaborablesEntre(proyecto.start_date, proyecto.end_date, festivos);
  // Fechas al reves o ventana vacia: no hay calendario contra el que medir.
  if (totalDias <= 0) return null;

  // diasLaborablesEntre devuelve NEGATIVO si la segunda fecha va antes que la
  // primera, que es justo lo que pasa con un proyecto que aun no ha empezado.
  // Sin acotar, el esperado saldria negativo.
  const crudo = diasLaborablesEntre(proyecto.start_date,
    hoy < proyecto.end_date ? hoy : proyecto.end_date, festivos);
  const idosDias = Math.max(0, Math.min(totalDias, crudo));
  const esperado = Math.max(0, Math.min(100, (idosDias / totalDias) * 100));

  const hayAvance = avance !== null && avance !== undefined;
  const ritmo = hayAvance && esperado > 0 ? Math.min(1, avance / esperado) : null;
  const cumplimiento = hayAvance && esperado > 0 ? Math.round((avance / esperado) * 100) : null;

  // Una banda de tolerancia de +-5 puntos: un 99% no es un proyecto en rojo.
  let estado = null;
  if (hoy < proyecto.start_date) estado = "sin-empezar";
  else if (cumplimiento === null) estado = null;
  else if (cumplimiento < 95)  estado = "atrasado";
  else if (cumplimiento > 105) estado = "adelantado";
  else estado = "aldia";

  const ETIQUETAS = {
    "sin-empezar": { etiqueta: "Sin empezar", color: "var(--text3)" },
    "atrasado":    { etiqueta: `${cumplimiento}% del plan`, color: "var(--red-text)" },
    "adelantado":  { etiqueta: `${cumplimiento}% del plan`, color: "#22c55e" },
    "aldia":       { etiqueta: "Al dia", color: "#22c55e" },
  };
  const { etiqueta = null, color = "var(--text3)" } = ETIQUETAS[estado] || {};

  return {
    totalDias, idosDias, esperado,
    avance: hayAvance ? avance : null,
    ritmo, cumplimiento,
    desfase: hayAvance ? Math.round(avance - esperado) : null,
    estado, etiqueta, color,
  };
}

// ── Salud ────────────────────────────────────────────────────────────────────
// Compuesto de avance, plazo y esfuerzo.
//
// Es OPINABLE a proposito, y por eso devuelve los tres sumandos por separado: un
// numero unico que nadie puede descomponer es un numero en el que no se puede
// confiar. La interfaz debe poder mostrarlos sueltos.
//
// Devuelve null si no hay con que calcularlo, en vez de un 0 que se leeria como
// "el proyecto va fatal".
export function salud(proyecto, tareas = [], minutosPorTarea = {}, festivos = {}, hoy = hoyISO()) {
  const avE = avanceEsfuerzo(tareas, minutosPorTarea);
  const avP = avancePasos(tareas);
  const avance = avE ?? avP;
  if (avance === null) return null;

  // Cuanto calendario ha pasado ya. Sin fechas de proyecto no se puede saber si
  // el avance va adelantado o atrasado, asi que ese sumando se omite.
  const ritmo = calendarioVsTrabajo(proyecto, avance, festivos, hoy)?.ritmo ?? null;

  const dp = desvioPlazo(tareas, festivos, hoy);
  // 10 dias laborables de retraso se considera el limite: mas alla, el sumando
  // ya esta a cero y no tiene sentido seguir castigando.
  const plazo = dp.max === null ? null : Math.max(0, 1 - Math.max(0, dp.max) / 10);

  const de = desvioEsfuerzo(tareas, minutosPorTarea);
  const esfuerzo = de.ratio === null ? null : Math.max(0, 1 - Math.max(0, de.ratio - 1));

  // Solo se promedian los sumandos que se han podido calcular, y se dice
  // cuantos: una salud sacada de un solo eje no vale lo mismo que una de tres.
  const partes = [
    { key: "ritmo",    label: "Avance frente al calendario", valor: ritmo,    peso: 0.4 },
    { key: "plazo",    label: "Plazo",                       valor: plazo,    peso: 0.3 },
    { key: "esfuerzo", label: "Esfuerzo",                    valor: esfuerzo, peso: 0.3 },
  ];
  const validas = partes.filter(p => p.valor !== null);
  if (!validas.length) return null;
  const pesoTotal = validas.reduce((s, p) => s + p.peso, 0);
  const valor = validas.reduce((s, p) => s + p.valor * p.peso, 0) / pesoTotal;

  return {
    valor: Math.round(valor * 100),
    partes,
    ejes: validas.length,
    // Con menos de tres ejes, el numero es mas fragil y hay que decirlo.
    completa: validas.length === 3,
  };
}

// ── Horas por semana ─────────────────────────────────────────────────────────
// Serie para el grafico de barras. Se agrupa por el LUNES de cada semana, la
// misma clave que usa el cuadrante de guardias, para no inventar una tercera
// forma de decir "semana".
export function horasPorSemana(porDia = []) {
  const lunesDe = iso => {
    const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
    const t = Date.UTC(y, m - 1, d);
    const dow = new Date(t).getUTCDay();          // 0 = domingo
    const atras = dow === 0 ? 6 : dow - 1;
    const l = new Date(t - atras * 86400000);
    return `${l.getUTCFullYear()}-${String(l.getUTCMonth() + 1).padStart(2, "0")}-${String(l.getUTCDate()).padStart(2, "0")}`;
  };
  const m = {};
  for (const f of porDia) {
    if (!f.entry_date) continue;
    const k = lunesDe(f.entry_date);
    m[k] = (m[k] || 0) + (Number(f.minutes) || 0);
  }
  return Object.entries(m)
    .map(([semana, minutes]) => ({ semana, minutes }))
    .sort((a, b) => a.semana.localeCompare(b.semana));
}
